// OpenTelemetry log transport to PostHog Logs. Server side only.
//
// Why OTel at all: `posthog-node@5.33.0` has no logs API (unlike `posthog-js`,
// which ships `captureLog` / `posthog.logger.*`). PostHog Logs is
// OTLP-native, so the server path is standard OTel packages pointed at
// PostHog's ingest endpoint. There is nothing PostHog-specific to install.
//
// This module only builds the exporter + processor. The provider is created by
// `@vercel/otel`'s `registerOTel({ logRecordProcessors })` in
// `instrumentation.ts` — see that file for why registration lives there.
// Feature code never touches this module; it goes through `log-server.ts`.
//
// REGION: the PostHog docs show `us.i.posthog.com`. Märka is on EU Cloud, so
// the endpoint MUST be the EU host — the same one `next.config.ts` rewrites
// `/ingest` to. Posting to the US host silently accepts nothing useful.
//
// AUTH: the `phc_` *project* token, same value the browser SDK uses. NOT a
// `phx_` personal API key; PostHog rejects those on this endpoint.
//
// PREREQUISITE: Logs must be enabled in the PostHog project settings before
// anything is accepted. Like the cookieless-hash-mode trap in
// `docs/observability.md`, a missing project setting looks exactly like
// success from this side — no error, no warning, just no data.
//
// WHY SimpleLogRecordProcessor AND NOT BatchLogRecordProcessor:
// `@vercel/otel` does NOT flush log records at request end. Its per-request
// `waitUntil(forceFlush)` hangs off a *span* processor wrapper whose
// `forceFlush()` iterates span processors only; the `LoggerProvider` is
// touched exclusively by `sdk.shutdown()`, which runs at process teardown, not
// per invocation. Verified by reading `@vercel/otel@2.1.3`'s `node/index.js`.
// So a batch processor on Vercel would buffer records and lose them whenever
// the function froze before the scheduled delay elapsed — dropping precisely
// the error logs this exists to capture.
//
// Exporting on emit matches the delivery-over-efficiency trade already made
// for `posthog-node` in `server.ts` (`flushAt: 1, flushInterval: 0`), for the
// same reason: Märka's v1 volume is small and a guaranteed write is worth more
// than a batched one. If log volume ever makes the per-record request cost
// real, the upgrade path is `BatchLogRecordProcessor` plus an awaited
// `flushServerLogs()` on every code path that emits — note that "every path"
// is the hard part, which is why v1 does not go there.
//
// `flushServerLogs()` exists because `onEmit` fires the export without
// awaiting it, so a freeze immediately after cuts the in-flight request —
// not a theoretical risk: in production NO server record survived until
// `logServer()` started registering `waitUntil(flushServerLogs())`, which
// holds the Vercel function open until pending exports settle.
// `flushPostHogServer()` also calls it, so the webhook's explicit flush
// covers logs too.

import { logs, SeverityNumber } from "@opentelemetry/api-logs";
import { OTLPLogExporter } from "@opentelemetry/exporter-logs-otlp-http";
import { type LogRecordProcessor, SimpleLogRecordProcessor } from "@opentelemetry/sdk-logs";

import { featureFlags } from "@/lib/feature-flags";

/** EU Cloud OTLP logs endpoint. See REGION note above. */
const POSTHOG_EU_LOGS_ENDPOINT = "https://eu.i.posthog.com/i/v1/logs";

/**
 * `service.name` for every record Märka emits server-side. Shows up as the
 * service filter in PostHog's Logs view and in `logs-attribute-values-list`.
 * The browser half sets `serviceName: "marka-web"` at init
 * (instrumentation-client.ts), so the two surfaces sit side by side in the
 * service dropdown.
 */
export const MARKA_SERVER_SERVICE_NAME = "marka-server";

/**
 * Null when logs should not be shipped, using the same gate as
 * `getPostHogServer()`: the `POSTHOG_ANALYTICS_MODE` kill switch plus the
 * presence of the project token. Callers wire the result unconditionally.
 */
export function buildLogRecordProcessors(): LogRecordProcessor[] | null {
  if (featureFlags.posthogAnalytics !== "enabled") {
    return null;
  }

  const token = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;
  if (!token) {
    return null;
  }

  const exporter = new OTLPLogExporter({
    url: POSTHOG_EU_LOGS_ENDPOINT,
    headers: { Authorization: `Bearer ${token}` },
  });

  return [new SimpleLogRecordProcessor({ exporter })];
}

/**
 * Emit the cold-start canary directly against the OTel API, bypassing
 * `logServer()`. Keeps the instrumentation entrypoint's import graph free of
 * the request-lifecycle machinery `logServer` carries for flush-on-freeze.
 * The canary has no request context to extend anyway; its export races the
 * cold start and is additionally drained by the first in-request log's
 * flush on the same instance.
 */
export function emitServerLoggingInitialized(): void {
  // Mirror logServer's stdout contract so the canary is greppable in Vercel
  // logs with the same shape as every other record.
  console.info(
    JSON.stringify({
      level: "info",
      event: "server_logging_initialized",
      service: MARKA_SERVER_SERVICE_NAME,
    })
  );
  logs.getLogger(MARKA_SERVER_SERVICE_NAME).emit({
    severityNumber: SeverityNumber.INFO,
    severityText: "info",
    body: "server_logging_initialized",
    attributes: { event: "server_logging_initialized" },
  });
}

/**
 * Await pending log exports. Safe to call unconditionally — resolves
 * immediately when no SDK provider was registered (kill switch off, token
 * missing, or edge runtime, where `register()` never wires logs).
 *
 * `@opentelemetry/api-logs` types the global provider without `forceFlush`
 * (it's an SDK concern, and the default is a no-op provider), so this feature
 * -detects rather than casting to the SDK class — which would also drag the
 * SDK into any bundle that imports this.
 */
export async function flushServerLogs(): Promise<void> {
  const provider: unknown = logs.getLoggerProvider();
  if (provider && typeof (provider as { forceFlush?: unknown }).forceFlush === "function") {
    await (provider as { forceFlush: () => Promise<void> }).forceFlush();
  }
}
